import { Button, Paper, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { distributionTotals } from '../../domain/distributionCalc';
import { compactMoney, date, money } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { AboutThisPage, PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';

export default function DistributionsPage() {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const fy = scope.distributions.filter(
    (item) => item.status === 'PAID' && item.paymentDate >= '2026-07-01',
  );
  const totals = fy.map(distributionTotals);
  const pending = scope.distributions.filter(
    (item) =>
      item.status === 'DRAFT' || item.status === 'PENDING_APPROVAL' || item.status === 'APPROVED',
  );
  return (
    <>
      <PageHeader
        title="Distributions"
        subtitle="Calculate per-holder entitlements from the register at the record date, apply withholding and DRP, and produce the bank file."
        actions={
          <Button component={RouterLink} to="/manager/distributions/new" variant="contained">
            New distribution
          </Button>
        }
      />
      <AboutThisPage>
        <p>
          The fund accountant provides the rate and tax components; the registry does the per-holder
          maths. TFN withholding (47%) applies where no TFN/ABN is quoted, and non-resident
          withholding applies to the interest component. Holders who elected DRP receive units at
          the current price instead of cash.
        </p>
        <p>
          Approval generates an ABA file for upload to the bank. Advices are published to the
          investor app on payment.
        </p>
      </AboutThisPage>
      <StatGrid>
        <StatCard
          label="Paid FY2027 (gross)"
          value={compactMoney(sumBy(totals, (item) => item.gross))}
          hint={`${fy.length} distributions`}
        />
        <StatCard
          label="Withheld FY2027"
          value={compactMoney(sumBy(totals, (item) => item.tfnWithholding + item.nrWithholding))}
          hint="Remitted to ATO monthly"
        />
        <StatCard
          label="Reinvested (DRP)"
          value={compactMoney(sumBy(totals, (item) => item.drp))}
        />
        <StatCard
          label="In progress"
          value={pending.length}
          tone={pending.length ? 'warning' : 'neutral'}
          hint="Draft, awaiting approval or payment"
        />
      </StatGrid>
      <Paper variant="outlined">
        <DataTable
          rows={scope.distributions}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/manager/distributions/${row.id}`)}
          defaultSort={{ key: 'pay', direction: 'desc' }}
          columns={[
            {
              key: 'fund',
              label: 'Fund / class',
              sortValue: (row) => scope.fundById.get(row.fundId)?.code ?? '',
              render: (row) => (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {scope.fundById.get(row.fundId)?.code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {scope.classById.get(row.classId)?.name}
                  </Typography>
                </>
              ),
            },
            {
              key: 'period',
              label: 'Period',
              sortValue: (row) => row.periodEnd,
              render: (row) => `${date(row.periodStart)} – ${date(row.periodEnd)}`,
            },
            {
              key: 'rate',
              label: 'Rate',
              align: 'right',
              render: (row) => `${row.centsPerUnit} cpu`,
            },
            {
              key: 'holders',
              label: 'Holders',
              align: 'right',
              hideBelow: 'md',
              render: (row) => row.lines.length,
            },
            {
              key: 'gross',
              label: 'Gross',
              align: 'right',
              sortValue: (row) => distributionTotals(row).gross,
              render: (row) => money(distributionTotals(row).gross),
            },
            {
              key: 'pay',
              label: 'Payment date',
              sortValue: (row) => row.paymentDate,
              render: (row) => date(row.paymentDate),
            },
            { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
          ]}
        />
      </Paper>
    </>
  );
}
